const { debounce, formatMoney } = window.BAO.Utils

class RangeSlider extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['min', 'max']
  }

  static get requiredElements () {
    return ['touchLeft', 'touchRight', 'line', 'min', 'max']
  }

  connectedCallback () {
    super.connectedCallback()

    this.startX = 0
    this.x = 0
    this.selectedTouchEl = null
    this.isBeingInteractedWith = false

    this.setTouchElPositions()
  }

  setupListeners () {
    super.setupListeners()

    this.onStart = this.onStart.bind(this)
    this.onStop = this.onStop.bind(this)
    this.onMove = this.onMove.bind(this)

    this.listeners.add(this.touchLeftEl, 'mousedown', this.onStart)
    this.listeners.add(this.touchRightEl, 'mousedown', this.onStart)
    this.listeners.add(this.touchLeftEl, 'touchstart', this.onStart)
    this.listeners.add(this.touchRightEl, 'touchstart', this.onStart)

    this.onResize = this.onResize.bind(this)
    this.debouncedResize = debounce(event => {
      this.onResize(event)
    }, 240)
    this.listeners.add(window, 'resize', this.debouncedResize)

    const detailsEl = this.closest('details')
    if (detailsEl) {
      this.listeners.add(
        detailsEl,
        'toggle',
        this.handleDetailsToggle.bind(this),
      )
    }
  }

  handleDetailsToggle (event) {
    if (event.target.open) {
      this.setTouchElPositions()
    }
  }

  onResize () {
    this.setTouchElPositions()
  }

  onStart (event) {
    if (this.isBeingInteractedWith || (event?.button && event?.button !== 0)) {
      return
    }

    this.isBeingInteractedWith = true

    // Prevent default dragging of selected content
    event.preventDefault()
    this.eventTouch = event.touches ? event.touches[0] : event

    this.selectedTouchEl =
      event.target.closest(this.els.touchLeft.selector) ||
      event.target.closest(this.els.touchRight.selector)

    this.x = this.selectedTouchEl?.matches(this.els.touchLeft.selector)
      ? this.touchLeftEl.offsetLeft
      : this.touchRightEl.offsetLeft
    this.startX = this.eventTouch.pageX - this.x

    this.listeners.add(document, 'mousemove', this.onMove)
    this.listeners.add(document, 'mouseup', this.onStop)
    this.listeners.add(document, 'touchmove', this.onMove)
    this.listeners.add(document, 'touchend', this.onStop)
  }

  onStop () {
    if (!this.isBeingInteractedWith) return

    this.listeners.remove(document, 'mousemove', this.onMove)
    this.listeners.remove(document, 'mouseup', this.onStop)
    this.listeners.remove(document, 'touchmove', this.onMove)
    this.listeners.remove(document, 'touchend', this.onStop)

    this.isBeingInteractedWith = false

    const changeEvent = new Event('change', {
      bubbles: true,
      cancelable: true,
    })

    if (
      this.els.min?.exists &&
      this.selectedTouchEl?.matches(this.els.touchLeft.selector)
    ) {
      this.els.min.element.dispatchEvent(changeEvent)
    }

    if (
      this.els.max?.exists &&
      this.selectedTouchEl?.matches(this.els.touchRight.selector)
    ) {
      this.els.max.element.dispatchEvent(changeEvent)
    }

    this.dispatchEvent(
      new CustomEvent('AFTER_RANGE_SLIDER_CHANGE', {
        bubbles: true,
        cancelable: true,
      }),
    )

    this.selectedTouchEl = null
  }

  onMove (event) {
    this.eventTouch = event.touches ? event.touches[0] : event

    this.x = this.eventTouch.pageX - this.startX

    if (this.selectedTouchEl?.matches(this.els.touchLeft.selector)) {
      if (
        this.x >
        this.touchRightEl.offsetLeft - this.selectedTouchEl.offsetWidth
      ) {
        this.x = this.touchRightEl.offsetLeft - this.selectedTouchEl.offsetWidth
      } else if (this.x < 0) {
        this.x = 0
      }

      this.selectedTouchEl.style.left = `${this.x}px`
      this.minValue = Math.floor(this.max * this.percentage)
      this.setHighlighterPosition(this.x, null)
    }

    if (this.selectedTouchEl?.matches(this.els.touchRight.selector)) {
      if (this.x < this.touchLeftEl.offsetLeft + this.touchLeftEl.offsetWidth) {
        this.x = this.touchLeftEl.offsetLeft + this.touchLeftEl.offsetWidth
      } else if (this.x > this.maxX) {
        this.x = this.maxX
      }
      this.selectedTouchEl.style.left = `${this.x}px`
      this.maxValue = Math.ceil(this.max * this.percentage)
      this.setHighlighterPosition(
        null,
        this.els.line.element.offsetWidth - this.x,
      )
    }
  }

  setTouchElPositions () {
    const leftX = (this.minValue / this.max) * this.els.line.element.offsetWidth
    const rightX =
      (this.maxValue / this.max) * this.els.line.element.offsetWidth

    this.touchLeftEl.style.left = `${leftX}px`
    this.touchRightEl.style.left = `${rightX}px`
    this.touchRightEl.style.right = 'unset'
    this.setHighlighterPosition(
      leftX,
      this.els.line.element.offsetWidth - rightX,
    )
  }

  setHighlighterPosition (leftX, rightX) {
    if (this.els?.highlighter?.exists) {
      if (leftX || leftX === 0) {
        this.els.highlighter.element.style.left = `${leftX}px`
      }

      if (rightX || rightX === 0) {
        this.els.highlighter.element.style.right = `${rightX}px`
      }
    }
  }

  get percentage () {
    return this.x / this.els.line.element.offsetWidth
  }

  get min () {
    return this.hasAttribute('min') ? parseFloat(this.getAttribute('min')) : 0
  }

  get max () {
    return this.hasAttribute('max') ? parseFloat(this.getAttribute('max')) : 100
  }

  get maxX () {
    return this.offsetWidth - this.touchRightEl.offsetWidth
  }

  set minValue (value) {
    this.setAttribute('min-value', value)

    if (this.els.min?.exists) {
      this.els.min.element.value = value
      this.els.touchLeft.element.setAttribute(
        'aria-label',
        formatMoney(value * 100).replace('.00', ''),
      )
      this.els.min.element.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this._minValue = value
  }

  get minValue () {
    if (!this._minValue) {
      return this.hasAttribute('min-value')
        ? parseFloat(this.getAttribute('min-value'))
        : this.min
    }

    return this._minValue
  }

  set maxValue (value) {
    if (this.els.max?.exists) {
      this.els.max.element.value = value
      this.els.touchRight.element.setAttribute(
        'aria-label',
        formatMoney(value * 100).replace('.00', ''),
      )
      this.els.max.element.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this._maxValue = value
  }

  get maxValue () {
    if (!this._maxValue) {
      return this.hasAttribute('max-value')
        ? parseFloat(this.getAttribute('max-value'))
        : this.max
    }

    return this._maxValue
  }

  get defaultMinValue () {
    const tmpMin = this.minValue >= this.min ? this.minValue : this.min
    return tmpMin > this.defaultMaxValue ? this.defaultMaxValue : tmpMin
  }

  get defaultMaxValue () {
    return this.maxValue >= this.max ? this.maxValue : this.max
  }

  get touchLeftEl () {
    return this.els.touchLeft.element
  }

  get touchRightEl () {
    return this.els.touchRight.element
  }

  get isBeingInteractedWith () {
    return this._isBeingInteractedWith
  }

  set isBeingInteractedWith (val) {
    this._isBeingInteractedWith = val
    this.classList.toggle('rng-Slider-active', val)
  }
}

if (!customElements.get('range-slider')) {
  customElements.define('range-slider', RangeSlider)
}
